import { createAsyncThunk } from "@reduxjs/toolkit";
import { requestsService } from "../services/requests.service";
import { gcpStorageService } from "../../../services/gcpStorageService";
import { TokenManager } from "../../../utils/storage/tokenmanager.storage";
import { handleRejectError } from "../../../utils/api/api.utils";
import {
  CreateRequestData,
  CreateMyRequestData,
  RequestFilters,
  ListRequestsApiResponse,
  MyRequestStatus,
  MyRequest,
} from "../interfaces/requests.interface";

/** Map backend status to My Requests UI display status. */
export const BACKEND_TO_STATUS: Record<string, MyRequestStatus> = {
  PENDING: "pending",
  FULFILLED: "completed",
  REJECTED: "expired",
  ONGOING: "ongoing",
};

// Create new request (accepts multiple backend response shapes)
export const createRequest = createAsyncThunk<
  { id: string; message: string },
  CreateRequestData
>("requests/createRequest", async (requestData, { rejectWithValue }) => {
  try {
    const response = await requestsService.createRequest(requestData);
    if (response.success && response.data) {
      return response.data;
    }
    // Backend may return { data: { id, ... } } without top-level success
    const data = response.data ?? response.request ?? response;
    if (data && typeof data === "object" && (data as any).id) {
      return {
        id: (data as any).id,
        message: (data as any).message ?? "Request created successfully",
      };
    }
    return rejectWithValue(response.message || "Failed to create request");
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Fetch requests with filters (response shape matches backend: { success, data, pagination })
export const fetchRequests = createAsyncThunk<
  ListRequestsApiResponse,
  RequestFilters | undefined
>("requests/fetchRequests", async (params, { rejectWithValue }) => {
  try {
    // UI is responsible for passing page/limit (defaults handled at call site).
    const response = (await requestsService.getRequests(
      params,
    )) as ListRequestsApiResponse;
    if (response.success && response.data != null && response.pagination != null) {
      return response;
    }
    return rejectWithValue("Failed to fetch requests");
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Create request from My Requests form (backend returns created request object only, no wrapper)
export const createMyRequest = createAsyncThunk<
  MyRequest,
  CreateMyRequestData
>("requests/createMyRequest", async (data, { rejectWithValue }) => {
  try {
    // Service returns response.data; backend returns the request object only
    const request = await requestsService.createMyRequest(data) as MyRequest;
    if (request && typeof request === "object" && request.id != null) {
      return request;
    }
    return rejectWithValue("Failed to create request");
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Update request status (PUT /requests/:id)
export const updateRequestStatus = createAsyncThunk<
  { requestId: string; status: string; updatedRequest: any },
  { requestId: string; status: string }
>("requests/updateRequestStatus", async ({ requestId, status }, { rejectWithValue }) => {
  try {
    const response = await requestsService.updateRequestStatus(requestId, status);
    const updatedRequest = response?.data ?? response;
    return { requestId, status, updatedRequest };
  } catch (error: any) {
    const message = error?.message ?? "Failed to update request status";
    return rejectWithValue(message);
  }
});

// Delete request
export const deleteRequest = createAsyncThunk<
  string,
  string
>("requests/deleteRequest", async (requestId, { rejectWithValue }) => {
  try {
    await requestsService.deleteRequest(requestId);
    return requestId;
  } catch (error: any) {
    const message = error?.message ?? "Failed to delete request";
    return rejectWithValue(message);
  }
});

// Upload request image via backend proxy (GCP, no CORS)
export const uploadRequestImage = createAsyncThunk<
  { url: string },
  { file: File }
>("requests/uploadRequestImage", async ({ file }, { rejectWithValue }) => {
  try {
    const userId = TokenManager.getUser()?.id ?? "";
    const { viewUrl } = await gcpStorageService.uploadRequestImageViaBackend(
      file,
      userId
    );
    return { url: viewUrl };
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});